import { Label } from "@/components/ui/label";

interface Permissions {
  dashboard: boolean;
  sales: boolean;
  salesOrders: boolean;
  salesCustomers: boolean;
  salesReport: boolean;
  menus: boolean;
  planner: boolean;
  profitGoal: boolean;
  analytics: boolean;
  library: boolean;
  libraryMyRecipes: boolean;
  libraryBentoRecipes: boolean;
  libraryStarterPack: boolean;
  pantry: boolean;
  pantryIngredients: boolean;
  pantryMaterials: boolean;
  pantrySuppliers: boolean;
  team: boolean;
  inventory: boolean;
  production: boolean;
  ai: boolean;
}

interface PlanPermissionsProps {
  permissions: Permissions;
  onChange: (permissions: Permissions) => void;
}

export function PlanPermissions({ permissions, onChange }: PlanPermissionsProps) {
  const updatePermission = (key: keyof Permissions, value: boolean) => {
    onChange({ ...permissions, [key]: value });
  };

  const updateParentPermission = (parent: 'library' | 'pantry' | 'sales', value: boolean) => {
    if (parent === 'library') {
      onChange({
        ...permissions,
        library: value,
        libraryMyRecipes: value,
        libraryBentoRecipes: value,
        libraryStarterPack: value,
      });
    } else if (parent === 'pantry') {
      onChange({
        ...permissions,
        pantry: value,
        pantryIngredients: value,
        pantryMaterials: value,
        pantrySuppliers: value,
      });
    } else {
      onChange({
        ...permissions,
        sales: value,
        salesOrders: value,
        salesCustomers: value,
        salesReport: value,
      });
    }
  };

  return (
    <div>
      <Label className="text-base font-semibold mb-3 block">Feature Access Permissions</Label>
      <div className="space-y-2 p-4 border rounded-lg max-h-96 overflow-y-auto">
        {/* Dashboard */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="perm-dashboard"
            checked={permissions.dashboard}
            onChange={(e) => updatePermission('dashboard', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <Label htmlFor="perm-dashboard" className="cursor-pointer font-medium">Dashboard</Label>
        </div>

        {/* Planner */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="perm-planner"
            checked={permissions.planner}
            onChange={(e) => updatePermission('planner', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <Label htmlFor="perm-planner" className="cursor-pointer font-medium">Planner</Label>
        </div>

        {/* Profit Goal */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="perm-profitGoal"
            checked={permissions.profitGoal}
            onChange={(e) => updatePermission('profitGoal', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <Label htmlFor="perm-profitGoal" className="cursor-pointer font-medium">Profit Goal</Label>
        </div>

        {/* Menus */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="perm-menus"
            checked={permissions.menus}
            onChange={(e) => updatePermission('menus', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <Label htmlFor="perm-menus" className="cursor-pointer font-medium">Menus</Label>
        </div>

        {/* Analytics */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="perm-analytics"
            checked={permissions.analytics}
            onChange={(e) => updatePermission('analytics', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <Label htmlFor="perm-analytics" className="cursor-pointer font-medium">Analytics</Label>
        </div>

        {/* Library */}
        <div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="perm-library"
              checked={permissions.library}
              onChange={(e) => updateParentPermission('library', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="perm-library" className="cursor-pointer font-medium">Library</Label>
          </div>
          <div className="ml-6 mt-1 space-y-1">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="perm-libraryMyRecipes"
                checked={permissions.libraryMyRecipes}
                onChange={(e) => updatePermission('libraryMyRecipes', e.target.checked)}
                disabled={!permissions.library}
                className="h-3 w-3 rounded border-gray-300"
              />
              <Label htmlFor="perm-libraryMyRecipes" className="cursor-pointer text-sm">My Recipes</Label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="perm-libraryBentoRecipes"
                checked={permissions.libraryBentoRecipes}
                onChange={(e) => updatePermission('libraryBentoRecipes', e.target.checked)}
                disabled={!permissions.library}
                className="h-3 w-3 rounded border-gray-300"
              />
              <Label htmlFor="perm-libraryBentoRecipes" className="cursor-pointer text-sm">ChefAiQ Recipes</Label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="perm-libraryStarterPack"
                checked={permissions.libraryStarterPack}
                onChange={(e) => updatePermission('libraryStarterPack', e.target.checked)}
                disabled={!permissions.library}
                className="h-3 w-3 rounded border-gray-300"
              />
              <Label htmlFor="perm-libraryStarterPack" className="cursor-pointer text-sm">Starter Pack</Label>
            </div>
          </div>
        </div>

        {/* Pantry */}
        <div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="perm-pantry"
              checked={permissions.pantry}
              onChange={(e) => updateParentPermission('pantry', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="perm-pantry" className="cursor-pointer font-medium">Pantry</Label>
          </div>
          <div className="ml-6 mt-1 space-y-1">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="perm-pantryIngredients"
                checked={permissions.pantryIngredients}
                onChange={(e) => updatePermission('pantryIngredients', e.target.checked)}
                disabled={!permissions.pantry}
                className="h-3 w-3 rounded border-gray-300"
              />
              <Label htmlFor="perm-pantryIngredients" className="cursor-pointer text-sm">Ingredients</Label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="perm-pantryMaterials"
                checked={permissions.pantryMaterials}
                onChange={(e) => updatePermission('pantryMaterials', e.target.checked)}
                disabled={!permissions.pantry}
                className="h-3 w-3 rounded border-gray-300"
              />
              <Label htmlFor="perm-pantryMaterials" className="cursor-pointer text-sm">Materials & Equipment</Label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="perm-pantrySuppliers"
                checked={permissions.pantrySuppliers}
                onChange={(e) => updatePermission('pantrySuppliers', e.target.checked)}
                disabled={!permissions.pantry}
                className="h-3 w-3 rounded border-gray-300"
              />
              <Label htmlFor="perm-pantrySuppliers" className="cursor-pointer text-sm">Suppliers</Label>
            </div>
          </div>
        </div>

        {/* Team */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="perm-team"
            checked={permissions.team}
            onChange={(e) => updatePermission('team', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <Label htmlFor="perm-team" className="cursor-pointer font-medium">Team</Label>
        </div>

        {/* Inventory */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="perm-inventory"
            checked={permissions.inventory}
            onChange={(e) => updatePermission('inventory', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <Label htmlFor="perm-inventory" className="cursor-pointer font-medium">Inventory</Label>
        </div>

        {/* Production */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="perm-production"
            checked={permissions.production}
            onChange={(e) => updatePermission('production', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <Label htmlFor="perm-production" className="cursor-pointer font-medium">Production</Label>
        </div>

        {/* AI Assistant */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="perm-ai"
            checked={permissions.ai}
            onChange={(e) => updatePermission('ai', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <Label htmlFor="perm-ai" className="cursor-pointer font-medium">AI Assistant</Label>
        </div>

        {/* Sales */}
        <div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="perm-sales"
              checked={permissions.sales}
              onChange={(e) => updateParentPermission('sales', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="perm-sales" className="cursor-pointer font-medium">Sales</Label>
          </div>
          <div className="ml-6 mt-1 space-y-1">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="perm-salesOrders"
                checked={permissions.salesOrders}
                onChange={(e) => updatePermission('salesOrders', e.target.checked)}
                disabled={!permissions.sales}
                className="h-3 w-3 rounded border-gray-300"
              />
              <Label htmlFor="perm-salesOrders" className="cursor-pointer text-sm">Orders</Label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="perm-salesCustomers"
                checked={permissions.salesCustomers}
                onChange={(e) => updatePermission('salesCustomers', e.target.checked)}
                disabled={!permissions.sales}
                className="h-3 w-3 rounded border-gray-300"
              />
              <Label htmlFor="perm-salesCustomers" className="cursor-pointer text-sm">Customers</Label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="perm-salesReport"
                checked={permissions.salesReport}
                onChange={(e) => updatePermission('salesReport', e.target.checked)}
                disabled={!permissions.sales}
                className="h-3 w-3 rounded border-gray-300"
              />
              <Label htmlFor="perm-salesReport" className="cursor-pointer text-sm">Sales Report</Label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
